import { Component, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { Icons } from '../../../../util/Icons';
import { SnackbarService } from '../../../../services/snackbar.service';
import { PermissionGroupService, Profile, UserService } from '@eadsysteme/ewsauthclient-v1';
import { DevService } from '../../../../services/dev.service';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { LayoutService } from '../../../../../@vex/services/layout.service';
import { UntypedFormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ConfirmationDialogComponent } from '../../../confirmation-dialog/confirmation-dialog.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'ara-support-tools',
	templateUrl: './support-tools.component.html',
	styleUrl: './support-tools.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslocoDirective, MatIcon, MatTooltip, MatFormField, MatInput, ReactiveFormsModule, FormsModule, MatError, MatButton],
})
export class SupportToolsComponent {
	translocoService = inject(TranslocoService);
	icons = inject(Icons);
	private snackbar = inject(SnackbarService);
	private userService = inject(UserService);
	private impersonationService = inject(DevService);
	dialog = inject(MatDialog);
	keycloakService = inject(KeycloakService);
	permissionGroupService = inject(PermissionGroupService);
	private layoutService = inject(LayoutService);

	imitateMail = signal('');
	isMobile = signal(false);
	errorMsg = signal('');
	allowUpdate = signal(false);
	deleteUserErrorMsg = signal('');
	allowDeleteUser = signal(false);
	currentMailFC = new UntypedFormControl('', [Validators.email, Validators.required, Validators.minLength(1)]);
	newMailFC = new UntypedFormControl('', [Validators.email, Validators.required, Validators.minLength(1)]);
	deleteUserFC = new UntypedFormControl('', [Validators.email, Validators.required, Validators.minLength(1)]);

	constructor() {
		this.isMobile.set(this.layoutService.isMobile());
	}

	public imitateUser() {
		if (!this.imitateMail()) {
			this.snackbar.error(this.translocoService.translate('settings.devSettings.supportTools.enterValidMail'));
			return;
		}
		this.userService.getImpersonatedUser(this.imitateMail()).subscribe({
			next: (token) => {
				this.impersonationService.exchangeKeycloakToken(token);
			},
			error: () => {
				this.snackbar.error(this.translocoService.translate('settings.devSettings.supportTools.userNotFound'));
			},
		});
	}

	public clearKeycloakCache() {
		this.permissionGroupService
			.clearKeycloakCache()
			.subscribe()
			.add(() => this.snackbar.success(this.translocoService.translate('settings.devSettings.supportTools.clearedKeycloakCache')));
	}

	public keycloakPassword() {
		this.keycloakService.login({
			action: 'UPDATE_PASSWORD',
		});
	}

	checkMailValidator() {
		this.currentMailFC.markAllAsTouched();
		this.newMailFC.markAllAsTouched();
		if (!this.currentMailFC.value && !this.newMailFC.value) {
			this.currentMailFC.setErrors(null);
			this.newMailFC.setErrors(null);
			this.errorMsg.set('');
			this.allowUpdate.set(false);
			return true;
		}
		const allowMailUpdate = this.currentMailFC.valid && this.newMailFC.valid;
		this.errorMsg.set(allowMailUpdate ? '' : this.translocoService.translate('settings.devSettings.supportTools.enterValidMail'));
		this.allowUpdate.set(allowMailUpdate);
	}

	checkDeleteUserValidator() {
		this.deleteUserFC.markAllAsTouched();
		if (!this.deleteUserFC.value) {
			this.deleteUserFC.setErrors(null);
			this.deleteUserErrorMsg.set('');
			this.allowDeleteUser.set(false);
			return true;
		}
		this.deleteUserErrorMsg.set(this.deleteUserFC.valid ? '' : this.translocoService.translate('settings.devSettings.supportTools.enterValidMail'));
		this.allowDeleteUser.set(this.deleteUserFC.valid);
	}

	confirmUpdateMail() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('settings.devSettings.supportTools.confirmNewMail');

		dialogRef.afterClosed().subscribe((val) => {
			if (val) this.updateMail();
		});
	}

	updateMail() {
		const mail: Profile = {
			email: this.newMailFC.value,
		};
		if (this.allowUpdate) {
			this.userService.updateUserMailAsAdmin(this.currentMailFC.value, mail).subscribe(
				(response) => {
					if (response) {
						this.snackbar.success(this.translocoService.translate('settings.devSettings.supportTools.mailUpdateSuccess'));
					} else this.snackbar.error(this.translocoService.translate('settings.devSettings.supportTools.mailUpdateBadInput'));
				},
				(err) => {
					this.snackbar.error(this.translocoService.translate('errors.mailUpdate'));
				}
			);
		}
	}

	confirmDeleteUser() {
		if (this.deleteUserFC.valid) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: false,
				autoFocus: false,
				restoreFocus: false,
			});
			dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
			dialogRef.componentInstance.confirmMessage = this.translocoService.translate('settings.devSettings.supportTools.confirmDeleteUser');

			dialogRef.afterClosed().subscribe((val) => {
				if (val) this.deleteUser();
			});
		}
	}

	deleteUser() {
		this.userService.deleteUserByUsernameAsAdmin(this.deleteUserFC.value).subscribe(
			(response) => {
				if (response) {
					this.snackbar.success(this.translocoService.translate('settings.devSettings.supportTools.deleteUserSuccess'));
				} else this.snackbar.error(this.translocoService.translate('settings.devSettings.supportTools.deleteUserBadInput'));
			},
			(err) => {
				this.snackbar.error(this.translocoService.translate('errors.deleteUser'));
			}
		);
	}
}
